<template>
  <UiComponentWrapper class="page">
    <!-- UI > Gradient -->
    <UiComponentWrapper class="gradient" />

    <!-- Section > Hero -->
    <UiComponentContainer
      v-if="data.page.heroTitle"
      class="jumbotron"
      :class="{ full: !!data.page.heroImage === false }"
    >
      <OrganismHero
        alignment="left"
        :primary-button="data.page.heroDisplayCallToActions
          ? {
            ...signupOrUpgradeCallToAction,
            label: 'Header section CTA',
          }
          : undefined
        "
        :secondary-button="data.page.heroDisplayCallToActions
          ? {
            ...requestDemoCallToAction,
            label: 'Header section CTA',
          }
          : undefined
        "
      >
        <template
          v-if="!!data.page.heroTag"
          #tag
        >
          {{ data.page.heroTag }}
        </template>
        <template
          v-if="!!data.page.heroTitle"
          #heading
        >
          {{ data.page.heroTitle }}
        </template>
        <template
          v-if="!!data.page.heroText"
          #paragraph
        >
          {{ data.page.heroText }}
        </template>
      </OrganismHero>
      <UiComponentWrapper
        v-if="!!data.page.heroImage"
        class="illustration"
      >
        <UiComponentImage
          :key="`hero-illustration-${heroImageWidth}`"
          :alt="data.page.heroImage.alt"
          :expected-size="heroImageWidth"
          :orientation="data.page.heroImage.width >= data.page.heroImage.height ? 'landscape' : 'portrait'"
          :height="data.page.heroImage.height"
          :source="data.page.heroImage.url"
          :width="data.page.heroImage.width"
        />
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- [Mobile & Tablet Only] Section > Table of content -->
    <UiComponentContainer
      v-if="titles.length > 0"
      ref="$tableOfContent"
      class="table-of-content"
    >
      <UiComponentParagraph>{{ data.componentGlobal.tableOfContentTitle }}</UiComponentParagraph>
      <UiComponentSelect
        id="select"
        :key="`table-of-content-${currentTitleIndex}`"
        :index="currentTitleIndex"
        :options="titles"
        @select:change="goTo(titles.findIndex((title) => title === $event), 80)"
      />
    </UiComponentContainer>

    <!-- Section > Content -->
    <UiComponentContainer class="content">
      <UiComponentWrapper class="share">
        <AtomAnchor
          :to="`https://twitter.com/intent/tweet?text=${publicRuntimeConfig.baseUrl}${path}`"
          @click="
            track('Share clicked', {
              category: 'Button',
              label: 'Twitter content',
            })
          "
        >
          <UiComponentIcon type="twitter" />
        </AtomAnchor>
        <AtomAnchor
          :to="`https://www.linkedin.com/sharing/share-offsite/?url=${publicRuntimeConfig.baseUrl}${path}`"
          @click="
            track('Share clicked', {
              category: 'Button',
              label: 'Linkedin content',
            })
          "
        >
          <UiComponentIcon type="linkedin" />
        </AtomAnchor>
        <AtomAnchor
          :to="`http://www.facebook.com/sharer.php?u=${publicRuntimeConfig.baseUrl}${path}`"
          @click="
            track('Share clicked', {
              category: 'Button',
              label: 'Facebook content',
            })
          "
        >
          <UiComponentIcon type="facebook" />
        </AtomAnchor>
      </UiComponentWrapper>

      <UiComponentWrapper class="main">
        <StructuredText
          :data="content"
          :render-block="renderBlock"
        />
      </UiComponentWrapper>

      <UiComponentWrapper class="aside">
        <UiComponentWrapper class="table-of-content">
          <UiComponentHeading
            as="paragraph"
            :level="5"
          >
            {{ data.componentGlobal.tableOfContentTitle }}
          </UiComponentHeading>
          <UiComponentWrapper class="separator" />
          <UiComponentWrapper class="titles">
            <UiComponentParagraph
              v-for="(value, index) in titles"
              :key="`title-${index}`"
              :class="{ active: index === currentTitleIndex }"
              @click="goTo(index)"
            >
              {{ value }}
            </UiComponentParagraph>
          </UiComponentWrapper>
        </UiComponentWrapper>

        <UiComponentWrapper
          v-if="hasATiedResource"
          class="resource"
        >
          <UiComponentHeading
            as="paragraph"
            :level="5"
          >
            {{ resource.title }}
          </UiComponentHeading>
          <AtomButton
            :action="resource.event && resource.event.name"
            :attributes="resource.event && resource.event.attributes"
            :icon-position="!resource.image || data.page.ctaCardSignupUpgradeOption ? 'icon-right' : 'icon-left'"
            :label="resource.label"
            :to="resource.to"
            type="text"
          >
            {{ resource.callToAction }}
            <UiComponentIcon :type="!resource.image || data.page.ctaCardSignupUpgradeOption ? 'arrow-right' : 'download'" />
          </AtomButton>
        </UiComponentWrapper>
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- [Conditional] Section > FAQ -->
    <UiComponentContainer
      v-if="faqItems.length > 0"
      class="faq"
      :start="1"
      :end="1"
    >
      <UiComponentHeading :level="2">
        {{ data.page.faqTitle || data.componentGlobal.faqTitle }}
      </UiComponentHeading>
      <OrganismAccordion :items="faqItems" />
      <AtomButton
        v-if="data.page.faqItems.length > 5"
        type="secondary"
        @click="toggleFaqItems"
      >
        {{
          faqItemsLenght === 5
            ? data.componentGlobal.faqShowMoreQuestions
            : data.componentGlobal.faqShowLessQuestions
        }}
      </AtomButton>
    </UiComponentContainer>

    <!-- Section > Get Started with Livestorm -->
    <UiComponentContainer class="get-started">
      <OrganismCallToAction
        :features="livestormFeatures"
        :primary-button="{
          ...signupOrUpgradeCallToAction,
          label: 'Big section CTA',
        }"
        :secondary-button="{
          ...requestDemoCallToAction,
          label: 'Big section CTA',
        }"
      >
        <template #heading>
          {{ data.componentGetStarted.title }}
        </template>
        <template #paragraph>
          {{ data.componentGetStarted.paragraph }}
        </template>
      </OrganismCallToAction>
    </UiComponentContainer>
  </UiComponentWrapper>
</template>

<script setup>
import { gsap } from 'gsap'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { StructuredText } from 'vue-datocms'

gsap.registerPlugin(ScrollToPlugin)
gsap.registerPlugin(ScrollTrigger)

// Layout
definePageMeta({
  layout: 'default',
  middleware: [
    'check-if-the-guide-exists',
  ],
})

// i18n
defineI18nRoute({
  locales: ['en', 'fr'],
  paths: {
    en: '/resources/guides/[slug]',
    fr: '/ressources/guides/[slug]',
  },
})

// Composables
const { page } = useAnalyticsJS()
const { setAvailableLanguages } = useAvailableLanguages()
const { updateListItems } = useBreadcrumb()
const { state } = useConditionalPreviewMode()
const { locale } = useI18n()
const { isLogged } = useIsLoggedIn()
const localePath = useLocalePath()
const { $md } = useMarkdown()
const { $slugify } = useNuxtApp()
const { setResource } = useResource()
const { meta, params, path } = useRoute()
const getRouteBaseName = useRouteBaseName()
const { public: publicRuntimeConfig } = useRuntimeConfig()
const { width: screenWidth } = useScreenSize()

if (['es'].includes(locale.value)) {
  throw createError({ statusCode: 404, message: 'Page not found' })
}

// Async Data
const { data } = await useAsyncData(
  `page-resources-guide-[slug]-${params.slug}-${locale.value}`,
  async () => {
    return Promise.all([
      $fetch(`/api/content/pages/resources-index/${locale.value}`, { query: { preview: state.preview } }),
      $fetch(`/api/content/pages/resources-list/${locale.value}`, { query: { preview: state.preview } }),
    ])
  },
  {
    transform: ([{ componentCreateAFreeAccount, componentGetStarted, componentGlobal, componentPromotion, componentResourceType, home, page: hub }, { allPillarPages }]) => {
      const page = allPillarPages.find(({ slug }) => slug === params.slug)
      return {
        componentCreateAFreeAccount,
        componentGetStarted,
        componentGlobal,
        componentPromotion,
        componentResourceType,
        home,
        hub,
        page,
      }
    },
  },
)

if (!data.value?.page) {
  throw createError({ statusCode: 404, message: 'Page not found' })
}

// Refs
const $h2 = ref([])
const $tableOfContent = ref(null)
const currentTitleIndex = ref(0)
const faqItemsLenght = ref(5)
const isAutoScrolling = ref(false)
const observer = ref(null)
const requestAnimationFrameId = ref(null)
const titles = ref([])

// Computed Properties
const content = computed(() => {
  return data.value.page?.content
})

const description = computed(() => {
  return data.value.page?.descriptionSeo
})

const faqItems = computed(() => {
  return data.value.page.faqItems
    .slice(0, faqItemsLenght.value)
    .map(({ title, text }) => ({
      title,
      content: $md.render(text),
    }))
})

const hasATiedResource = computed(() => {
  return !!(data.value.page.ctaCardResource || data.value.page.ctaCardSignupUpgradeOption)
})

const heroImageWidth = computed(() => {
  switch (true) {
    case screenWidth.value < 1024:
      return undefined
    case screenWidth.value >= 1024 && screenWidth.value < 1328:
      return (screenWidth.value - 48) * (620 / 1280)
    default:
      return 620
  }
})

const imageWidth = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return screenWidth.value - 32
    case screenWidth.value >= 768 && screenWidth.value < 1328:
      return Math.min(Math.max(screenWidth.value - 48, 0), 730)
    default:
      return 730
  }
})

const livestormFeatures = computed(() => {
  return [
    data.value.componentGlobal.noCreditCard,
    data.value.componentGlobal.noInstall,
  ]
})

const signupOrUpgradeCallToAction = computed(() => {
  return isLogged.value
    ? {
        action: 'Upgrade clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/settings?page=settings&tab=billing`,
        slot: data.value.componentGlobal.ctaUpgradeNow,
      }
    : {
        action: 'Signup clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/signup`,
        slot: data.value.componentGlobal.ctaTryForFree,
      }
})

const requestDemoCallToAction = computed(() => {
  return {
    action: 'Demo clicked',
    slot: data.value.componentGlobal.ctaGetDemo,
    to: '#form-demo',
  }
})

const resource = computed(() => {
  return hasATiedResource.value
    ? {
        backgroundImage: data.value.page.ctaCardResource?.backgroundImage,
        callToAction: data.value.page.ctaCardSignupUpgradeOption
          ? signupOrUpgradeCallToAction.value.slot
          : data.value.page.ctaCardResource.resourceType.cta,
        coMarketing: data.value.page.ctaCardResource?.coMarketing,
        description: data.value.page.ctaCardResource?.shortDescription,
        event: {
          name: data.value.page.ctaCardSignupUpgradeOption
            ? signupOrUpgradeCallToAction.value.action
            : 'Offer clicked',
          attributes: {
            label: 'Sidebar section CTA',
          },
        },
        image: data.value.page.ctaCardResource?.image,
        label: 'Sidebar section CTA',
        tagColor: data.value.page.ctaCardResource?.resourceType?.color,
        tagIcon: data.value.page.ctaCardResource?.resourceType?.icon,
        title: data.value.page.ctaCardSignupUpgradeOption
          ? data.value.componentCreateAFreeAccount.text
          : data.value.page.ctaCardResource?.title,
        to: data.value.page.ctaCardSignupUpgradeOption
          ? signupOrUpgradeCallToAction.value.to
          : isURL(data.value.page.ctaCardResource?.slug)
            ? data.value.page.ctaCardResource?.slug
            : '#form-ebook',
        trackName: data.value.page.ctaCardResource?.trackName || data.value.page.ctaCardResource?.trackNameFallback,
        type: data.value.page.ctaCardResource?.resourceType?.name,
        url: localePath({
          name: 'file-slug',
          params: {
            slug: data.value.page.ctaCardResource?.ebook?.slug,
          },
        }),
      }
    : null
})

const title = computed(() => {
  return data.value.page?.titleSeo
})

// Methods
function animateProgressBar() {
  gsap.to('.app__layout-header .progress .bar', {
    ease: 'none',
    scrollTrigger: {
      start: 0,
      end: 'max',
      scrub: true,
    },
    width: '100%',
  })
}

function getCallToActionBySlug(callToAction) {
  if (callToAction.to === `https://${publicRuntimeConfig.appBaseUrl}/#/signup`) {
    return signupOrUpgradeCallToAction.value
  }
  else if (callToAction.to === '#form-demo') {
    return requestDemoCallToAction.value
  }
  else {
    return callToAction
  }
}

function goTo(index, offset = 0) {
  const { offsetTop } = $h2.value[index]

  gsap.to(window, {
    duration: 1.4,
    ease: 'expo.inOut',
    onComplete: () => {
      isAutoScrolling.value = false
    },
    onStart: () => {
      currentTitleIndex.value = index
      isAutoScrolling.value = true
    },
    scrollTo: {
      y: offsetTop - offset,
    },
  })
}

function isSticky(element) {
  if ('sticky' !== getComputedStyle(element).position) {
    return false
  }

  return (
    1
    >= element.getBoundingClientRect().top
    - parseInt(getComputedStyle(element).top)
  )
}

function isURL(to) {
  const pattern
    = /[-a-zA-Z0-9@:%_+.~#?&//=]{2,256}\.[a-z]{2,4}\b(\/[-a-zA-Z0-9@:%_+.~#?&//=]*)?/gi
  return pattern.test(to)
}

function onObserve(entries) {
  if (isAutoScrolling.value) {
    return
  }

  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      currentTitleIndex.value = $h2.value.findIndex(element => element === entry.target)
    }
  })
}

function onRequestAnimationFrame() {
  toggleSticky($tableOfContent.value.$el)

  requestAnimationFrameId.value = window.requestAnimationFrame(
    onRequestAnimationFrame,
  )
}

function renderBlock({ record, key }) {
  const UiComponentImage = resolveComponent('UiComponentImage')
  const MoleculeCallToAction = resolveComponent('MoleculeCallToAction')
  const MoleculeCustomerQuote = resolveComponent('MoleculeCustomerQuote')
  const MoleculeDefinition = resolveComponent('MoleculeDefinition')
  const MoleculeTextImage = resolveComponent('MoleculeTextImage')
  const MoleculeTextYoutubeIframe = resolveComponent('MoleculeTextYoutubeIframe')
  const OrganismContentCard = resolveComponent('OrganismContentCard')
  const OrganismHero = resolveComponent('OrganismHero')
  const OrganismPlayerVideo = resolveComponent('OrganismPlayerVideo')
  const OrganismRelatedPosts = resolveComponent('OrganismRelatedPosts')
  const OrganismRichText = resolveComponent('OrganismRichText')
  const OrganismTabs = resolveComponent('OrganismTabs')
  const OrganismTestimonials = resolveComponent('OrganismTestimonials')
  const UiComponentWrapper = resolveComponent('UiComponentWrapper')

  switch (record.__typename) {
    case 'AtomImageRecord':
      return h(
        UiComponentWrapper,
        {
          key,
          class: 'image-full',
        },
        () => [
          h(UiComponentImage, {
            alt: record.image.alt,
            expectedSize: imageWidth.value,
            height: record.image.height,
            orientation: record.image.width >= record.image.height ? 'landscape' : 'portrait',
            source: record.image.url,
            width: record.image.width,
          }),
        ],
      )
    case 'AtomVideoRecord':
      return h(
        UiComponentWrapper,
        {
          key,
          class: 'video-full',
        },
        () => [
          h(OrganismPlayerVideo, {
            posterSrc: record.video.data.thumbnailUrl,
            videoSrc: record.video.data.streamingUrl,
          }),
        ],
      )
    case 'ContentYoutubeVideoRecord':
      return h(
        UiComponentWrapper,
        {
          key,
        },
        () => [
          h(OrganismRichText, {
            class: 'w-full',
            content: [
              {
                __typename: 'ContentYoutubeVideoRecord',
                youtubeLink: record.youtubeLink,
              },
            ],
          }),
        ],
      )
    case 'MoleculeContentHeaderRecord':
      return h(
        UiComponentWrapper,
        {
          key,
        },
        () => [
          h(OrganismHero, {
            class: 'content-header',
            alignment: 'left',
            level: 2,
            primaryButton: getCallToActionBySlug(
              record.callToAction,
            ),
          }, {
            heading: () => record.heading,
            paragraph: () => record.paragraph,
            tag: () => (record.tag === '' ? undefined : record.tag),
          }),
        ],
      )
    case 'MoleculeDefinitionRecord':
      return h(
        UiComponentWrapper,
        {
          key,
        },
        () => [
          h(
            MoleculeDefinition,
            {
              level: record.titleLevel,
              title: record.title,
            },
            () => [
              h('div', {
                innerHTML: $md.render(record.content),
              }),
            ],
          ),
        ],
      )
    case 'MoleculeQuoteRecord':
      return h(MoleculeCustomerQuote, {
        key,
        quote: {
          content: record.testimonial.quote,
          customer: {
            avatar: {
              ...record.testimonial.avatar,
              src: record.testimonial.avatar.url,
            },
            name: record.testimonial.fullname,
            position: record.testimonial.occupation,
          },
          logo: {
            ...record.testimonial.logo,
            src: record.testimonial.logo.url,
          },
        },
      })
    case 'MoleculeSmallCallToActionRecord':
      return h(
        UiComponentWrapper,
        {
          key,
        },
        () => [
          h(MoleculeCallToAction, {
            callToAction: {
              ...signupOrUpgradeCallToAction.value,
              label: 'Small section CTA',
            },
            ...data.value.componentPromotion,
          }, {
            heading: () => record.heading,
            paragraph: () => record.paragraph,
          }),
        ],
      )
    case 'MoleculeTextImageRecord':
      return h(
        UiComponentWrapper,
        {
          key,
        },
        () => [
          h(MoleculeTextImage, {
            direction: record.direction,
            image: record.image,
            level: record.titleLevel,
            maxImageHeight: 366,
            maxImageWidth: 290,
          }, {
            heading: () => record.heading,
            paragraph: () =>
              h(OrganismRichText, {
                class: 'whitespace-normal',
                content: [
                  {
                    __typename: 'ContentTextHtmlRecord',
                    text: record.paragraph,
                  },
                ],
              }),
          }),
        ],
      )
    case 'MoleculeTextOnlyRecord':
      return h(
        UiComponentWrapper,
        {
          key,
        },
        () => [
          h('div', {
            innerHTML: $md.render(record.content),
          }),
        ],
      )
    case 'MoleculeTextVideoRecord':
      return h(
        UiComponentWrapper,
        {
          key,
        },
        () => [
          h(MoleculeTextYoutubeIframe, {
            level: record.titleLevel,
            video: {
              ...record.video,
            },
          }, {
            heading: () => record.heading,
            paragraph: () =>
              h(OrganismRichText, {
                class: 'whitespace-normal',
                content: [
                  {
                    __typename: 'ContentTextHtmlRecord',
                    text: record.paragraph,
                  },
                ],
              }),
          }),
        ],
      )
    case 'OrganismContentCardRecord':
      return h(
        UiComponentWrapper,
        {
          key,
        },
        () => [
          h(OrganismContentCard, {
            as: 'paragraph',
            content: {
              backgroundImage: record.content.backgroundImage,
              callToAction: record.content.resourceType.cta,
              description: record.content.shortDescription,
              image: record.content.image,
              tagColor: record.content.resourceType.color,
              tagIcon: record.content.resourceType.icon,
              title: record.content.title,
              to: record.content.slug.includes('http')
                ? record.content.slug
                : localePath({
                  name: 'resources-type-slug',
                  params: {
                    slug: record.content.slug,
                    type: $slugify(
                      record.content.resourceType.name,
                    ),
                  },
                }),
              type: record.content.resourceType.name,
            },
            highlighted: true,
          }),
        ],
      )
    case 'OrganismRelatedPostListRecord':
      return h(
        UiComponentWrapper,
        {
          key,
        },
        () => [
          h(OrganismRelatedPosts, {
            posts: record.posts
              .filter((_, index) => index < 2)
              .map(post => ({
                category: post.blogPostCategory[0]?.name,
                content: post.content,
                coverImage: post.coverWithImgix || post.coverImage,
                date: post.date,
                slug: `blog/${post.slug}`,
                title: post.title,
              })),
          }, {
            default: () => record.heading,
          }),
        ],
      )
    case 'OrganismTabRecord':
      return h(
        UiComponentWrapper,
        {
          key,
        },
        () => [
          h(OrganismTabs, {
            tabs: record.tabs.map(tab => ({
              id: tab.id,
              title: tab.title,
              content: $md.render(tab.content),
            })),
          }),
        ],
      )
    case 'OrganismTestimonialListRecord':
      return h(
        UiComponentWrapper,
        {
          key,
        },
        () => [
          h(OrganismTestimonials, {
            testimonials: record.testimonials
              .filter(testimonial => testimonial.authorUserStory)
              .map(testimonial => ({
                customer: {
                  avatar: {
                    alt: `${testimonial.authorUserStory}' Avatar`,
                    height: testimonial.authorPicture.height,
                    url: testimonial.authorPicture.url,
                    width: testimonial.authorPicture.width,
                  },
                  name: testimonial.authorUserStory,
                  position: testimonial.authorPosition,
                },
                logo: {
                  alt: `${testimonial.name}' logo`,
                  height: testimonial.logoColor.height,
                  url: testimonial.logoColor.url,
                  width: testimonial.logoColor.width,
                },
                quote: testimonial.quote,
              })),
          }),
        ],
      )
    default:
      return null
  }
}

function toggleFaqItems() {
  faqItemsLenght.value === 5
    ? (faqItemsLenght.value = data.value.page.faqItems.length)
    : (faqItemsLenght.value = 5)
}

function toggleSticky(element) {
  if (isSticky(element)) {
    element.classList.add('is-sticky')
  }
  else {
    element.classList.remove('is-sticky')
  }
}

// Lifecycle Hooks
onBeforeUnmount(() => {
  if (observer.value) {
    observer.value.disconnect()
    observer.value = null
  }

  if (requestAnimationFrameId.value) {
    window.cancelAnimationFrame(requestAnimationFrameId.value)
  }

  ScrollTrigger.killAll()
})

onMounted(() => {
  animateProgressBar()
  $h2.value = [...document.querySelectorAll('.page .content .main h2')]
  observer.value = new IntersectionObserver(onObserve)
  $h2.value.forEach((element) => {
    observer.value.observe(element)
  })
  page(
    data.value.page.trackName || data.value.page.trackNameFallback,
  )
  setAvailableLanguages(data.value.page.alternativeVersions.map(({ locale }) => locale))
  updateListItems([
    {
      item: localePath('index'),
      name: data.value.home.trackingBreadcrumbTitle,
    },
    {
      item: localePath('resources'),
      name: data.value.hub.trackingBreadcrumbTitle,
    },
    {
      item: localePath({ name: getRouteBaseName() }),
      name: data.value.page.trackingBreadcrumbTitle || title.value,
    },
  ])
  titles.value = $h2.value
    .filter(element => !!element.tagName)
    .map(element => element.textContent)

  if (hasATiedResource.value) {
    setResource({
      ...resource.value,
      event: {
        name: 'Offer clicked',
        attributes: {
          label: 'Thank you pop up CTA',
        },
      },
      to: resource.value.url,
    })
  }

  nextTick(() => {
    if (titles.value.length > 0) {
      requestAnimationFrameId.value = window.requestAnimationFrame(
        onRequestAnimationFrame,
      )
    }
  })
})

// Meta
const { alternativeVersions } = data.value.page
const locales = alternativeVersions.map(({ locale }) => locale)

meta.nuxtI18n = alternativeVersions.reduce(
  (accumator, { locale, value: slug }) => ({
    ...accumator,
    [locale]: { slug },
  }),
  {},
)

const head = useLocaleHead({
  identifierAttribute: 'id',
  addSeoAttributes: true,
})

// Meta
useSeoMeta({
  title: title,
  description: description,
  ogTitle: title,
  ogDescription: description,
  twitterTitle: title,
  twitterDescription: description,
})

useHead({
  link: head.value.link
    .filter((link) => {
      return link.rel === 'canonical'
        || (link.rel === 'alternate' && link.hreflang === 'x-default')
        || (link.rel === 'alternate' && locales.includes(link.hreflang))
    })
    .map((link) => {
      return {
        ...link,
        href: !locales.includes('en') && link.id === 'i18n-xd'
          ? `${publicRuntimeConfig.baseUrl}${localePath({ name: getRouteBaseName() })}`
          : link.href,
      }
    }),
  meta: head.value.meta.filter((meta) => {
    return meta.property === 'og:url'
      || meta.property === 'og:locale'
      || (meta.property === 'og:locale:alternate' && locales.includes(meta.content))
  }),
  script: [
    faqItems.value.length > 0
      ? {
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            'mainEntity': faqItems.value.map(({ title, content }) => ({
              '@type': 'Question',
              'name': title,
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': content,
              },
            })),
          }),
          type: 'application/ld+json',
        }
      : undefined,
    ...data.value.page.structuredData.map(({ contentData }) => ({
      innerHTML: JSON.stringify({
        ...contentData,
      }),
      type: 'application/ld+json',
    })),
    {
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': [
          {
            '@type': 'ListItem',
            'position': 1,
            'name': data.value.home.trackingBreadcrumbTitle,
            'item': `${publicRuntimeConfig.baseUrl}${localePath('index')}`,
          },
          {
            '@type': 'ListItem',
            'position': 2,
            'name': data.value.hub.trackingBreadcrumbTitle,
            'item': `${publicRuntimeConfig.baseUrl}${localePath('resources')}`,
          },
          {
            '@type': 'ListItem',
            'position': 3,
            'name': data.value.page.trackingBreadcrumbTitle || title.value,
          },
        ],
      }),
      type: 'application/ld+json',
    },
  ],
})
</script>

<style lang="postcss" scoped>
.page {
  @apply relative flex-col items-center;

  .gradient {
    @apply absolute -top-14 w-full h-screen bg-gradient-to-b from-[#F6F7F9] to-white z-0;

    @screen xl {
      @apply -top-[72px];
    }
  }

  .jumbotron {
    @apply flex-col items-start pt-10 pb-6 z-10;

    @screen md {
      @apply py-14;
    }

    @screen xl {
      @apply flex-row items-center justify-between;
    }

    &.full {
      @apply justify-center;

      .hero {
        @screen xl {
          @apply w-full;
        }
      }
    }

    .hero {
      @screen xl {
        @apply w-span6;
      }

      &.alignment-left {
        :deep(.tag) {
          @apply self-start;
        }

        :deep(.h1),
        :deep(.description), {
          @apply text-left;
        }

        :deep(.button-list) {
          @apply justify-start;
        }
      }
    }

    .illustration {
      @apply hidden;

      @screen xl {
        @apply flex w-span6;
      }
    }
  }

  > .table-of-content {
    @apply sticky top-14 py-4 flex-col self-start gap-1;

    @screen xl {
      @apply hidden;
    }

    &.is-sticky {
      @apply bg-white z-20 shadow-light2;
    }

    p {
      @apply font-medium text-sm text-winter-green-900 leading-6 z-0;
    }
  }

  > .content {
    @apply flex-col z-10 mt-6;

    @screen xl {
      @apply flex-row justify-between mt-26;
    }

    .share {
      @apply hidden;

      @screen xl {
        @apply sticky top-24 flex flex-col self-start items-center gap-6 w-span1;
      }

      .icon {
        @apply text-grey-blue-600 transition-colors duration-500 ease-in-out;

        &:hover {
          @apply text-grey-blue-800;
        }
      }
    }

    :deep(.main) {
      @screen xl {
        @apply w-span7;
      }

      > div {
        @apply flex flex-col items-center w-full;

        > * {
          @apply mt-14;

          @screen mdminus {
            @apply mt-16;
          }

          @screen mdplus {
            @apply mt-26;
          }

          &:first-child {
            @apply mt-0;
          }
        }

        .no-margin-top {
          @apply mt-0 !important;
        }

        .content-card {
          @screen xl {
            @apply max-h-none h-auto;
          }

          .head {
            @screen xl {
              @apply flex-shrink-0 h-full max-h-[274px] w-[290px];
            }

            .ebook {
              &.landscape {
                @screen xl {
                  width: calc(100% - 38px);
                }
              }
            }
          }

          .body {
            @screen xl {
              @apply w-full px-10 !important;
            }

            .button {
              .h2 {
                @screen xl {
                  @apply line-clamp-2;
                }
              }
            }

            .description {
              @screen xl {
                @apply line-clamp-2;
              }
            }
          }
        }

        .image-full {
          @apply justify-center;
        }

        .video-full {
          @apply justify-center;

          .video {
            @apply h-auto max-w-[730px] rounded-2xl;

            video {
              @apply h-auto rounded-2xl;
            }
          }
        }

        .quote {
          @apply bg-grey-blue-100;
        }

        .small-call-to-action {
          .left {
            .h4 {
              @apply font-medium text-white text-xl leading-7 !important;

              @screen mdminus {
                @apply text-2xl leading-8 !important;
              }

              @screen mdplus {
                @apply text-2xl leading-8 !important;
              }
            }

            .h4 + p {
              @apply mt-2 text-grey-blue-100 !important;
            }
          }
        }

        .text-image {
          @screen md {
            @apply flex-row items-center justify-between;
          }

          &.left {
            .image {
              @screen md {
                @apply order-first mr-10 mt-0;
              }
            }
          }

          &.right {
            .image {
              @screen md {
                @apply ml-10 mt-0;
              }
            }
          }

          .text {
            @screen md {
              @apply w-full p-0 m-0;
            }
          }

          .text .rich-text p {
            @apply text-grey-blue-600 !important;
          }

          .image {
            @apply flex-shrink-0 h-[366px] bg-grey-blue-100;

            @screen md {
              @apply w-[290px];
            }
          }
        }

        .text-iframe {
          @screen md {
            @apply flex-col items-start;
          }

          .iframe {
            @apply order-first bg-grey-blue-100 rounded-4xl overflow-hidden;

            @screen md {
              @apply max-w-none w-full pb-[56.25%];
            }

            @screen xl {
              @apply w-[730px];
            }
          }

          .text {
            @apply pl-0 mt-8;
          }
        }
      }

      .related-posts {
        @apply relative flex-col gap-4 p-8 bg-grey-blue-100 rounded-4xl;

        @screen md {
          @apply p-12;
        }

        .head {
          .line {
            @apply hidden;
          }

          p {
            @apply px-0 font-medium text-xl text-winter-green-900 leading-7 normal-case !important;

            @screen md {
              @apply text-2xl leading-8 !important;
            }
          }
        }

        .body {
          @apply flex-col gap-10 mt-0;

          @screen md {
            @apply flex-row flex-wrap justify-between;
          }

          .related-post {
            @apply w-full;

            @screen md {
              @apply mx-0;
              width: calc((100% - 40px) / 2);
            }

            .tag {
              @apply mt-4 h-6 px-2 bg-blizzard-100 text-blizzard-700 text-xs transform-none leading-4 rounded-xs;
            }

            .anchor {
              @apply mt-3;

              .h4 {
                @apply font-medium text-winter-green-900 text-origin leading-7 !important;

                @screen md {
                  @apply text-xl !important;
                }
              }
            }

            .description {
              @apply mt-1 text-sm text-grey-blue-600 leading-6 !important;

              .date {
                @apply hidden;
              }
            }
          }
        }
      }

      .call-to-action {
        .content {
          p {
            @apply text-livestorm-blue-200 !important;
          }

          .items {
            li {
              &:before {
                content: "\e83f";
              }
            }
          }
        }
      }

      h1,
      h2,
      h3,
      h4,
      h5,
      h6 {
        @apply text-winter-green-900 whitespace-pre-line;
      }

      h1,
      h2 {
        @apply font-normal;

        b,
        strong {
          @apply font-normal text-livestorm-blue-700;
        }
      }

      h3,
      h4,
      h5,
      h6 {
        @apply font-medium;

        b,
        strong {
          @apply font-medium;
        }
      }

      h1 {
        @apply text-4xl leading-14;

        @screen mdminus {
          @apply text-5xl leading-16;
        }

        @screen mdplus {
          @apply text-6xl leading-20;
        }

        b,
        strong {
          @apply font-normal;
        }
      }

      h2 {
        @apply text-3xl leading-10;

        @screen mdminus {
          @apply font-medium text-3xl leading-10;
        }

        @screen mdplus {
          @apply font-normal text-5xl leading-16;
        }

        b,
        strong {
          @apply font-normal text-livestorm-blue-700;

          @screen mdminus {
            @apply font-medium;
          }

          @screen mdplus {
            @apply font-normal;
          }
        }
      }

      h3 {
        @apply text-2xl leading-8;

        @screen mdminus {
          font-size: 28px;
          line-height: 34px;
        }

        @screen mdplus {
          @apply text-3xl leading-10;
        }

        b,
        strong {
          @apply font-medium text-blizzard-700;
        }
      }

      h4 {
        @apply text-xl leading-7;

        @screen mdminus {
          @apply text-2xl leading-8;
        }

        @screen mdplus {
          @apply text-2xl leading-8;
        }

        b,
        strong {
          @apply text-winter-green-700;
        }
      }

      h5 {
        @apply text-origin leading-7;

        @screen mdminus {
          @apply text-xl;
        }

        @screen mdplus {
          @apply text-xl;
        }

        b,
        strong {
          @apply text-sunburst-red-700;
        }
      }

      h6 {
        @apply text-origin leading-7;
      }

      p,
      .rich-text p {
        @apply font-normal text-grey-blue-600 text-origin leading-7 !important;
      }

      blockquote {
        @apply p-8 bg-livestorm-blue-100 rounded-4xl;

        @screen md {
          @apply p-10;
        }

        p {
          @apply inline text-center text-livestorm-blue-700 text-xl leading-[160%] !important;

          &::before,
          &::after {
            @apply inline;
          }

          &::before {
            @apply content-['“'];
          }

          &::after {
            @apply content-['”'];
          }
        }
      }

      ol {
        @apply pl-8;

        li {
            @apply font-normal text-origin text-grey-blue-600 leading-7 list-decimal list-inside  !important;

            &::marker {
              @apply text-lg leading-7 font-medium text-livestorm-blue-700;
            }
          }
      }

      ul {
        @apply pl-8;

        li {
          @apply block font-normal text-origin text-grey-blue-600 leading-7 !important;

          &:before {
            @apply inline-block w-3 h-3 bg-livestorm-blue-700 mr-3 content-[''] rounded-[3px];
          }
        }
      }

      strong {
        @apply font-medium;
      }

      a {
        @apply text-livestorm-blue-700;

        &:hover {
          @apply text-livestorm-blue-800;
        }

        &.button {
          &.primary {
            @apply text-white;
          }
        }
      }

      h1 + h1,
      h1 + h2,
      h1 + h3,
      h2 + h1,
      h2 + h2,
      h2 + h3,
      h3 + h1,
      h3 + h2,
      h3 + h3,
      h4 + h1,
      h4 + h2,
      h4 + h3,
      h5 + h1,
      h5 + h2,
      h5 + h3,
      h6 + h1,
      h6 + h2,
      h6 + h3,
      h1 + blockquote,
      h2 + blockquote,
      h3 + blockquote,
      h1 + p,
      h2 + p,
      h3 + p,
      h1 + ol,
      h2 + ol,
      h3 + ol,
      h1 + ul,
      h2 + ul,
      h3 + ul {
        @apply mt-4;
      }

      h1 + h4,
      h1 + h5,
      h1 + h6,
      h2 + h4,
      h2 + h5,
      h2 + h6,
      h3 + h4,
      h3 + h5,
      h3 + h6,
      h4 + h4,
      h4 + h5,
      h5 + h6,
      h5 + h4,
      h5 + h5,
      h5 + h6,
      h5 + h6,
      h6 + h4,
      h6 + h5,
      h6 + h6,
      h4 + blockquote,
      h5 + blockquote,
      h6 + blockquote,
      h4 + p,
      h5 + p,
      h6 + p,
      h4 + ol,
      h5 + ol,
      h6 + ol,
      h4 + ul,
      h5 + ul,
      h6 + ul,
      p + ol,
      p + ul {
        @apply mt-3;
      }

      p + h1,
      p + h2,
      ol + h1,
      ol + h2,
      ul + h1,
      ul + h2 {
        @apply mt-26;
      }

      p + h3,
      p + h4,
      p + h5,
      p + h6,
      ol + h3,
      ol + h4,
      ol + h5,
      ol + h6,
      ul + h3,
      ul + h4,
      ul + h5,
      ul + h6 {
        @apply mt-8;
      }

      blockquote + p,
      p + blockquote,
      p + p,
      ol + p,
      ul + p,
      li + li {
        @apply mt-4;
      }
    }

    .aside {
      @apply hidden;

      @screen xl {
        @apply sticky top-24 flex flex-col self-start gap-8 w-span4 pl-[110px];
      }

      .table-of-content {
        @apply flex-col;

        .separator {
          @apply h-px my-6 bg-[#EAEEF1];
        }

        .titles {
          @apply flex-col gap-2;

          p {
            @apply text-sm text-grey-blue-600 leading-6 transform transition-all duration-500 ease-in-out cursor-pointer;

            &.active {
              @apply text-winter-green-900 translate-x-2;
            }
          }
        }
      }

      .resource {
        @apply flex-col items-start gap-2 p-6 pb-4 bg-white border-2 border-grey-blue-200 rounded-[24px];

        .button {
          @apply self-start px-0 font-bold text-livestorm-blue-700 leading-none;
        }
      }
    }
  }

  .faq {
    @apply flex-col items-center gap-16 mt-14;

    @screen md {
      @apply mt-26;
    }

    @screen xl {
      @apply mt-46;
    }

    .h2 {
      @apply text-center;
    }
  }

  .get-started {
    @apply mt-20;

    @screen md {
      @apply mt-34;
    }
  }
}
</style>
